import React, { useState, useEffect, useRef } from 'react';
import './SpeakingTest.css';
import '../../componets/Listening.css';
import EngdemyLogo from '../../assets/Images/EngdemyUploaded.png';
import { isTestUnlocked } from '../../utils/subscriptionHelper';
import { fetchQuestions } from '../../api/index';
import { saveSpeakingAttempt } from "../../api/index";
import { startRecording as startRecordingUtility, stopRecording as stopRecordingUtility } from './Common';
import ScoreGraph from './ScoreGraph';
import startAudio from '../../assets/Audios/celpip_beep.wav';
import timeUpAudio from '../../assets/Audios/timeup.mp3';
import LoadingAnimation from '../../pages/LoadingAnimation';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { useSelector, useDispatch } from "react-redux";
import { FaRegClock } from "react-icons/fa";

const SpeakingTest = ({ auth }) => {

    const dispatch = useDispatch();
    // Use the same logic as ReadingTest.js for user retrieval
    const reduxUser = useSelector((state) => state.auth?.authData);
    const getStoredUser = () => {
        try {
            if (reduxUser?.result) return reduxUser.result;
            if (reduxUser?.email || reduxUser?._id) return reduxUser;
            const raw = localStorage.getItem("user_info");
            if (raw) {
                const localUser = JSON.parse(raw);
                if (localUser?.result) return localUser.result;
                if (localUser?.email || localUser?._id) return localUser;
            }
            const authRaw = localStorage.getItem("authData");
            if (authRaw) {
                const localAuth = JSON.parse(authRaw);
                if (localAuth?.result) return localAuth.result;
                if (localAuth?.email || localAuth?._id) return localAuth;
            }
        } catch (e) {}
        return null;
    };
    const [currentUser, setCurrentUser] = useState(getStoredUser);
        
    // Sync Redux with localStorage if not in Redux
    useEffect(() => {
        const storedUser = getStoredUser();
        if (storedUser) {
            setCurrentUser(storedUser);
        }
        if (!reduxUser && storedUser) {
            const raw = localStorage.getItem("user_info");
            let dataToDispatch = null;
            try {
                const parsed = raw ? JSON.parse(raw) : null;
                if (parsed?.result) dataToDispatch = parsed;
            } catch (e) {}
            if (!dataToDispatch) {
                dataToDispatch = { result: storedUser, ...storedUser };
            }
            dispatch({ type: "AUTH", data: dataToDispatch });
        }
    }, [reduxUser, dispatch]);


    const userId = currentUser?._id;
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    // Restrict access for unpaid users or tests not purchased (same as ReadingTest.js)
    useEffect(() => {
        const testNum = parseInt(params.testNumber || params.id || params.number || location.pathname.split('/').pop());
        if (!isTestUnlocked(testNum)) {
            alert("This test is locked. Please upgrade your account to access it.");
            navigate("/pricing");
        }
    }, [params, location, navigate]);

    // Handle showResults from CheckProgress
    useEffect(() => {
        if (location.state?.showResults && location.state?.savedAttempts) {
             const savedAttempts = location.state.savedAttempts;
             const results = {};
             
             if (Array.isArray(savedAttempts)) {
                 savedAttempts.forEach(att => {
                    const question = att.questionId;
                    if (question && question.taskNumber) {
                         results[question.taskNumber] = {
                            transcription: att.transcription,
                            scores: att.scores,
                            taskTitle: question.taskTitle || `Task ${question.taskNumber}`,
                            taskNumber: question.taskNumber
                         };
                    }
                 });
                 
                 if (Object.keys(results).length > 0) {
                     setAllTaskResults(results);
                     setTestCompleted(true);
                     // Also set selectedTestNumber so the UI knows which test we are viewing
                     if (savedAttempts[0]?.questionId?.testNumber) {
                        setSelectedTestNumber(parseInt(savedAttempts[0].questionId.testNumber));
                     }
                 }
             }
        }
    }, [location.state]);

    // Place isTestAccessible here, before any usage!
    const isTestAccessible = (testNum) => {
        return isTestUnlocked(testNum);
    };

    // Extract test number from URL - handle multiple possible parameter names
    const urlTestNumber = params.testNumber || params.id || params.number || location.pathname.split('/').pop();

    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(null);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [prepareTime, setPrepareTime] = useState(0);
    const [recordingTime, setRecordingTime] = useState(0);
    const [isRecording, setIsRecording] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);
    const [isRecordingDone, setIsRecordingDone] = useState(false);
    const [imageSelectionTime, setImageSelectionTime] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showThirdImage, setShowThirdImage] = useState(false);
    const [task5Stage, setTask5Stage] = useState(1); // 1: Instructions, 2: Choose Option, 3: Compare & Persuade
    const [isStopping, setIsStopping] = useState(false);
    const [transcription, setTranscription] = useState(null);
    const [analysis, setAnalysis] = useState(null);
    const [scores, setScores] = useState({});
    const [showGraph, setShowGraph] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [preCheckFailed, setPreCheckFailed] = useState(false);
        const [selectedTestNumber, setSelectedTestNumber] = useState(null);
        const [justifications, setJustifications] = useState({});
    const [testCompleted, setTestCompleted] = useState(false);
    const [allTaskResults, setAllTaskResults] = useState({}); // { taskNumber: { transcription, scores, taskTitle } }

    // Instructions screen & sample response modal states
    const [showInstructions, setShowInstructions] = useState(!location.state?.showResults);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [showSampleModal, setShowSampleModal] = useState(false);
    const [sampleAudioPlaying, setSampleAudioPlaying] = useState(false);

    // Cancel speech synthesis when component unmounts
    useEffect(() => {
        return () => {
            if (window.speechSynthesis) {
                window.speechSynthesis.cancel();
            }
        };
    }, []);

    // Speech synthesis for instructions screen
    const toggleSpeech = () => {
        if (!window.speechSynthesis) {
            alert('Speech synthesis is not supported in this browser.');
            return;
        }

        if (isSpeaking) {
            window.speechSynthesis.cancel();
            setIsSpeaking(false);
            return;
        }

        const bullets = [
            "Speaking Test Instructions.",
            "In this practice test, recording will be done for each of your speaking tasks, and a score will be added upon completion. You will also receive detailed AI evaluation and performance analysis.",
            "For this practice test, an automated preparation timer and recording timer will guide you to ensure you complete each task within the given time.",
            "Audio recording will be done automatically using your computer microphone. Please speak clearly into your microphone when the recording timer begins.",
            "On the official test, if you do not finish a task in the time provided, the screen will move to the next task. You cannot go back to the previous task. However, in this practice test, in order to move forward in the test you must click on NEXT."
        ];

        const textToRead = bullets.join(' ');
        const utterance = new SpeechSynthesisUtterance(textToRead);
        utterance.rate = 0.95;
        utterance.pitch = 1;

        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);

        setIsSpeaking(true);
        window.speechSynthesis.speak(utterance);
    };

    const stopSpeech = () => {
        if (window.speechSynthesis) {
            window.speechSynthesis.cancel();
        }
        setIsSpeaking(false);
        setSampleAudioPlaying(false);
    };

    const toggleSampleAudio = (text) => {
        if (!window.speechSynthesis) {
            alert('Speech synthesis is not supported in this browser.');
            return;
        }

        if (sampleAudioPlaying) {
            window.speechSynthesis.cancel();
            setSampleAudioPlaying(false);
            return;
        }

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.92;
        utterance.pitch = 1;

        utterance.onend = () => setSampleAudioPlaying(false);
        utterance.onerror = () => setSampleAudioPlaying(false);

        setSampleAudioPlaying(true);
        window.speechSynthesis.speak(utterance);
    };

    const audioContextRef = useRef(null);
    const gumStreamRef = useRef(null);

    const recRef = useRef(null);
    const recordingIntervalRef = useRef(null);
    const recordingTimeRef = useRef(recordingTime);

    useEffect(() => {
        recordingTimeRef.current = recordingTime;
    }, [recordingTime]);

    // audio play with synth beep fallback
    const playAudio = (audioSrc) => {
        try {
            const audio = new Audio(audioSrc);
            audio.play().catch((error) => {
                console.warn('Audio play failed, using fallback:', error);
                if (audioSrc === startAudio) {
                    playSynthBeep();
                }
            });
        } catch (err) {
            console.error('Error playing audio:', err);
            if (audioSrc === startAudio) {
                playSynthBeep();
            }
        }
    };

    const playSynthBeep = () => {
        try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (AudioContextClass) {
                const ctx = new AudioContextClass();
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.setValueAtTime(950, ctx.currentTime);
                gain.gain.setValueAtTime(0.4, ctx.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.55);
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start();
                osc.stop(ctx.currentTime + 0.55);
            }
        } catch (e) {
            console.warn('Synth beep failed:', e);
        }
    };

    useEffect(() => {
        if (!auth.authData) {
            navigate('/login');
        }
    }, [auth, navigate]);

    // Handle test number from URL param
    useEffect(() => {
        const testNum = parseInt(urlTestNumber);
        if (testNum >= 1 && testNum <= 50) {
            if (!isTestAccessible(testNum)) {
                alert("This test is locked. Please upgrade your account to access it.");
                navigate("/pricing");
                return;
            }

            setSelectedTestNumber(testNum);
            if (location.state?.showResults) {
                setShowInstructions(false);
                setCurrentIndex(0);
            } else {
                setShowInstructions(true);
                setCurrentIndex(null);
            }
        }
    }, [urlTestNumber, navigate, location.state]);

    // Fetch questions
    useEffect(() => {
        const getQuestions = async () => {
            try {
                const { data } = await fetchQuestions(selectedTestNumber);
                // Filter questions for the selected testNumber and restrict to tasks 1 to 8
                // const filteredQuestions = data.filter(q => q.testNumber === testNumber.toString()).slice(0, 8);
                //setQuestions(filteredQuestions);
                setQuestions(data);
            } catch (error) {
                console.error("Error fetching questions:", error);
            }
        };

        // If a testNumber is selected, fetch the questions
        if (selectedTestNumber) {
            getQuestions();
        }
    }, [selectedTestNumber]);


    useEffect(() => {
        if (currentIndex !== null && currentIndex < questions.length) {
            const question = questions[currentIndex];
            setCurrentQuestion(question);

            if (String(question.taskNumber) === '5') {
                setTask5Stage(1);
                setPrepareTime(0);
                setImageSelectionTime(null);
                setSelectedImage(null);
                setShowThirdImage(false);
            } else {
                setPrepareTime(question.preparationTime);
                setImageSelectionTime(null);
            }

            setRecordingTime(question.recordingTime);
            resetStates();
        }
    }, [currentIndex, questions]);

    const resetStates = () => {
        setTranscription(null);
        setAnalysis(null);
        setScores({});
        setAudioUrl(null);
        setIsRecordingDone(false);
        setShowGraph(false);
        setPreCheckFailed(false);
    };

    useEffect(() => {
        if (!showInstructions && prepareTime > 0) {
            const countdownInterval = setInterval(() => {
                setPrepareTime((prevTime) => {
                    if (prevTime <= 1) {
                        clearInterval(countdownInterval);
                        startActualRecording();
                        return 0;
                    }
                    return prevTime - 1;
                });
            }, 1000);

            return () => clearInterval(countdownInterval);
        }
    }, [prepareTime, showInstructions]);

    useEffect(() => {
        let selectionCountdown;
        if (!showInstructions && String(currentQuestion?.taskNumber) === '5' && task5Stage === 2 && imageSelectionTime !== null && imageSelectionTime > 0) {
            selectionCountdown = setInterval(() => {
                setImageSelectionTime((prevTime) => {
                    if (prevTime <= 1) {
                        return 0;
                    }
                    return prevTime - 1;
                });
            }, 1000);
        } else if (!showInstructions && String(currentQuestion?.taskNumber) === '5' && task5Stage === 2 && imageSelectionTime === 0) {
            // Auto advance when selection time ends
            if (!selectedImage) {
                setSelectedImage('img1');
            }
            setTask5Stage(3);
            setShowThirdImage(true);
            setImageSelectionTime(null);
            setPrepareTime(currentQuestion?.preparationTime || 60);
        }

        return () => {
            if (selectionCountdown) clearInterval(selectionCountdown);
        };
    }, [imageSelectionTime, currentQuestion, task5Stage, showInstructions, selectedImage]);

    useEffect(() => {
        if (isRecording) {
            recordingIntervalRef.current = setInterval(() => {
                setRecordingTime((prevTime) => {
                    if (prevTime <= 1) {
                        clearInterval(recordingIntervalRef.current);
                        debouncedStopActualRecording();
                        playAudio(timeUpAudio);
                        return 0;
                    }
                    return prevTime - 1;
                });
            }, 1000);
        }

        return () => {
            if (recordingIntervalRef.current) {
                clearInterval(recordingIntervalRef.current);
            }
        };
    }, [isRecording]);

    
    const nextQuestion = () => {
        // Save current task result before moving on
        if (currentQuestion) {
            setAllTaskResults(prev => ({
                ...prev,
                [currentQuestion.taskNumber]: {
                    transcription: transcription || '',
                    scores: scores || {},
                    taskTitle: currentQuestion.taskTitle || `Task ${currentQuestion.taskNumber}`,
                    taskNumber: currentQuestion.taskNumber
                }
            }));
        }

        if (currentIndex < questions.length - 1) {
            saveTestAttempt();
            setCurrentIndex((prevIndex) => prevIndex + 1);
        } else {
            // Last task completed - show feedback page
            saveTestAttempt();
            setTestCompleted(true);
        }
    };

    
    const handleGlobalNext = () => {
        stopSpeech();
        if (showInstructions) {
            setShowInstructions(false);
            setCurrentIndex(0);
            return;
        }

        const isTask5 = String(currentQuestion?.taskNumber) === '5';

        if (isTask5) {
            if (task5Stage === 1) {
                // Move from Instructions (Screen 1) to Choose Option (Screen 2)
                setTask5Stage(2);
                setImageSelectionTime(60);
                return;
            }

            if (task5Stage === 2) {
                // Move from Choose Option (Screen 2) to Comparison/Persuading (Screen 3)
                if (!selectedImage) {
                    setSelectedImage('img1');
                }
                setTask5Stage(3);
                setShowThirdImage(true);
                setImageSelectionTime(null);
                setPrepareTime(currentQuestion?.preparationTime || 60);
                return;
            }

            if (task5Stage === 3) {
                if (prepareTime > 0) {
                    // Skip prep and start recording!
                    setPrepareTime(0);
                    startActualRecording();
                    return;
                }

                if (isRecording) {
                    debouncedStopActualRecording();
                }

                nextQuestion();
                return;
            }
        }

        if (prepareTime > 0) {
            // User clicked NEXT during preparation -> skip prep and start recording!
            setPrepareTime(0);
            startActualRecording();
            return;
        }

        if (isRecording) {
            debouncedStopActualRecording();
        }

        nextQuestion();
    };

    const handleGlobalBack = () => {
        stopSpeech();
        if (showInstructions) {
            navigate('/dashboard?section=speaking');
            return;
        }

        const isTask5 = String(currentQuestion?.taskNumber) === '5';

        if (isTask5) {
            if (task5Stage === 3) {
                if (isRecording) {
                    debouncedStopActualRecording();
                }
                setTask5Stage(2);
                setImageSelectionTime(60);
                setPrepareTime(0);
                return;
            }
            if (task5Stage === 2) {
                setTask5Stage(1);
                setImageSelectionTime(null);
                return;
            }
            if (task5Stage === 1) {
                if (currentIndex > 0) {
                    setCurrentIndex(currentIndex - 1);
                } else {
                    setShowInstructions(true);
                }
                return;
            }
        }

        if (currentIndex > 0) {
            if (isRecording) {
                debouncedStopActualRecording();
            }
            setCurrentIndex(currentIndex - 1);
        } else {
            if (isRecording) {
                debouncedStopActualRecording();
            }
            setShowInstructions(true);
        }
    };

    
    
    


    const startActualRecording = () => {
        setIsRecording(true);
        playAudio(startAudio);
        startRecordingUtility(audioContextRef, gumStreamRef, recRef, setRecordingTime);
    };

    const API_URL = (process.env.REACT_APP_API_BASE_URL && process.env.REACT_APP_API_BASE_URL.startsWith("http") && !process.env.REACT_APP_API_BASE_URL.includes("localhost:5000"))
        ? process.env.REACT_APP_API_BASE_URL.replace(/\/$/, '')
        : "https://engdemyeducation.com";

    const stopActualRecording = async () => {
        if (isStopping || !isRecording) return;
        setIsStopping(true);
        setIsRecording(false);
        setIsRecordingDone(true);

        let audioBlob = null;
        try {
            audioBlob = await stopRecordingUtility(recRef);
        } catch (err) {
            console.error("Error stopping recording utility:", err);
            audioBlob = new Blob([], { type: "audio/webm" });
        }

        let transcriptionData = null;

        if (audioBlob && audioBlob.size > 0) {
            const formData = new FormData();
            formData.append("audio", audioBlob, "audio.webm");

            try {
                setAudioUrl(URL.createObjectURL(audioBlob));
            } catch (e) {
                console.error("Error creating object URL:", e);
            }

            try {
                setIsLoading(true);
                const uploadResponse = await fetch(`${API_URL}/upload`, {
                    method: "POST",
                    body: formData,
                });

            if (!uploadResponse.ok) {
                throw new Error(`Failed to upload: ${uploadResponse.statusText}`);
            }

            const uploadData = await uploadResponse.json();

            const transcriptionResponse = await fetch(`${API_URL}/upload/transcribe`, {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ filePath: uploadData.filePath })
            });

            if (!transcriptionResponse.ok) {
                throw new Error(`Failed to transcribe: ${transcriptionResponse.statusText}`);
            }

            transcriptionData = await transcriptionResponse.json();
            setTranscription(transcriptionData.transcription);

        } catch (error) {
            console.error("Error:", error);
        } finally {
            setIsLoading(false);
            setIsStopping(false);
        }
    }

        if (transcriptionData && transcriptionData.transcription) {
            try {
                setIsLoading(true);
                const duration = currentQuestion ? (currentQuestion.recordingTime - recordingTimeRef.current) : 0;
                const questionText = currentQuestion ? `${currentQuestion.questionTitle || ''}\n${currentQuestion.questionContent || ''}\n${currentQuestion.questionContentExtra || ''}` : '';

                const analysisResponse = await fetch(`${API_URL}/analyze`, {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        transcription: transcriptionData.transcription,
                        question: questionText,
                        duration: duration
                    })
                });

                if (!analysisResponse.ok) {
                    throw new Error(`Failed to analyze: ${analysisResponse.statusText}`);
                }

                const analysisData = await analysisResponse.json();
                setAnalysis(analysisData.analysis);

                // Check if the server flagged a pre-check failure
                if (analysisData.preCheckFailed) {
                    setPreCheckFailed(true);
                    setTranscription(null); // hide raw transcription
                    setScores({});
                } else {
                    setPreCheckFailed(false);
                    const extractedScores = analysisData.extractedScores;
                    setScores(extractedScores);

                    // save with transcription, extractedScores and audioBlob
                    await saveTestAttempt(transcriptionData.transcription, extractedScores, audioBlob);
                }
            } catch (error) {
                console.error("Error analyzing transcription:", error);
            } finally {
                setIsLoading(false);
                setIsStopping(false);
            }
        } else {
            console.error("Transcription data is not available for analysis.");
        }
    };

    // Example: set justifications after analysis (customize as needed)
    useEffect(() => {
        if (analysis) {
            // Example: generate justifications from analysis (customize logic)
            setJustifications(analysis.justifications || {});
        }
    }, [analysis]);

    const saveTestAttempt = async (transcription, scores = {}, audioBlob = null) => {
        try {
            const userId = currentUser?._id;
            const questionId = currentQuestion?._id || currentQuestion?.id;
            if (!userId) {
                console.error("No userId provided. Cannot save speaking attempt.");
                return;
            }
            if (!questionId) {
                console.error("No questionId provided. Cannot save speaking attempt.");
                return;
            }
            // Prepare FormData for multipart upload
            const formData = new FormData();
            if (audioBlob) {
                formData.append("audio", audioBlob, "audio.webm");
            }
            formData.append("transcription", transcription || "");
            formData.append("userId", userId);
            formData.append("questionId", questionId);

            // Append scores
            Object.keys(scores).forEach(key => {
                formData.append(`scores[${key}]`, scores[key]);
            });

            // Append justifications
            Object.keys(justifications).forEach(key => {
                formData.append(`justifications[${key}]`, justifications[key]);
            });

            // Call API
            const response = await saveSpeakingAttempt(formData);

            console.log(
                "Speaking attempt saved successfully for userId:",
                userId,
                "questionId:",
                questionId,
                "Response:",
                response.data
            );
        } catch (error) {
            console.error("Error saving speaking attempt for userId:", userId, "Error:", error);
        }
    };

    const debounce = (func, delay) => {
        let timeout;
        return function (...args) {
            if (timeout) clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), delay);
        };
    };
    const debouncedStopActualRecording = debounce(stopActualRecording, 300);

    
    const getSpeakingImageUrl = (taskNumber, filename) => {
        if (!filename) return '';
        if (filename.startsWith('http://') || filename.startsWith('https://')) return filename;
        return `/speakingimages/Task${taskNumber}/${filename}`;
    };

    const getPartnerChoiceTitle = () => {
        const extra = (currentQuestion?.questionContentExtra || '').toLowerCase();
        const title = (currentQuestion?.questionTitle || '').toLowerCase();
        if (extra.includes('family') || title.includes('family')) {
            return "Your Family's Choice";
        }
        if (extra.includes('friend') || title.includes('friend')) {
            return "Your Friend's Choice";
        }
        if (extra.includes('colleague') || title.includes('colleague')) {
            return "Your Colleague's Choice";
        }
        return "Your Business Partner's Choice";
    };

    const renderFormattedCaption = (text) => {
        if (!text) return null;
        let clean = text.replace(/\*\*/g, '').trim();
        clean = clean.replace(/\\r\\n|\\r|\\n/g, '\n');

        // 1. Protect price ranges split across newlines like '$50\n- $80' or '$50 - $80' or '$50-$80'
        clean = clean.replace(/(\$\d+)\s*[\r\n]+\s*-\s*(\$?\d+)/gi, '$1__DASH__$2');
        clean = clean.replace(/(\$\d+)\s*-\s*(\$?\d+)/g, '$1__DASH__$2');
        clean = clean.replace(/(\d+)\s*-\s*(\d+\s*(?:sq\.?\s*ft|feet|ft|floor|desks|months?|years?|rolls?))/gi, '$1__DASH__$2');

        const cleanItem = (str) => {
            if (!str) return '';
            return str
                .replace(/^[•\-\*]\s*/, '')
                .replace(/\.$/, '')
                .replace(/__DASH__/g, ' - ')
                .trim();
        };

        let title = '';
        let bullets = [];

        // If string has newlines:
        if (clean.includes('\n')) {
            const rawLines = clean.split('\n').map(l => l.trim()).filter(Boolean);
            let items = [];
            rawLines.forEach(line => {
                // Check if this line is a run-on sentence paragraph like "Tropical Fruit Paradise. The cake is topped..."
                if (/\.\s+[A-Z0-9$]/.test(line) && !line.startsWith('-') && !line.startsWith('•')) {
                    const sents = line.split(/\.\s+(?=[A-Z0-9$])/).map(s => s.trim()).filter(Boolean);
                    items.push(...sents);
                } else if (/\s+[-•*]\s+/.test(line)) {
                    const parts = line.split(/\s+[-•*]\s+/).map(p => p.trim()).filter(Boolean);
                    items.push(...parts);
                } else {
                    items.push(line);
                }
            });

            if (items.length > 0) {
                if (/^[•\-\*]\s*/.test(items[0])) {
                    title = '';
                    bullets = items.map(cleanItem).filter(Boolean);
                } else {
                    title = cleanItem(items[0].replace(/[:-]+$/, ''));
                    bullets = items.slice(1).map(cleanItem).filter(Boolean);
                }
            }
        } else if (/\s+[-•*]\s+/.test(clean)) {
            // Case 2: bullet markers like " - " or " • "
            const parts = clean.split(/\s+[-•*]\s+/).map(p => p.trim()).filter(Boolean);
            title = cleanItem(parts[0].replace(/[:-]+$/, ''));
            bullets = parts.slice(1).map(cleanItem).filter(Boolean);
        } else if (/\.\s+/.test(clean)) {
            // Case 3: sentences separated by period + space
            const parts = clean.split(/\.\s+/).map(p => p.trim()).filter(Boolean);
            title = cleanItem(parts[0].replace(/[:-]+$/, ''));
            bullets = parts.slice(1).map(cleanItem).filter(Boolean);
        } else if (/\s{2,}/.test(clean)) {
            // Case 4: multiple spaces
            const parts = clean.split(/\s{2,}/).map(p => p.trim()).filter(Boolean);
            if (parts.length > 1) {
                title = cleanItem(parts[0].replace(/[:-]+$/, ''));
                bullets = parts.slice(1).map(cleanItem).filter(Boolean);
            } else {
                title = cleanItem(clean);
            }
        } else {
            title = cleanItem(clean);
        }

        return (
            <div className="paragon-task5-caption-box">
                {title && <div className="paragon-task5-caption-title">{title}</div>}
                {bullets.length > 0 && (
                    <ul className="paragon-task5-caption-list">
                        {bullets.map((b, idx) => (
                            <li key={idx} className="paragon-task5-caption-item">
                                <span className="paragon-caption-bullet">-</span>
                                <span className="paragon-caption-text">{b}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
    };


    
    // Screen 1: Authentic Instructions Screen matching Screenshot 1
    const renderTask5Screen1 = () => (
        <div className="paragon-speaking-card paragon-task5-instructions-card">
            <div className="paragon-speaking-prompt-container">
                <span className="paragon-info-badge" style={{ marginTop: 2, flexShrink: 0 }}>i</span>
                <div className="paragon-speaking-prompt-text">
                    <h3 className="paragon-task5-instructions-heading">Instructions</h3>
                    <div className="paragon-task5-instructions-body">
                        <p className="paragon-task5-intro-text">This task is made up of THREE parts:</p>
                        <ol className="paragon-task5-parts-list">
                            <li>Choose an option</li>
                            <li>Preparation time</li>
                            <li>Speaking</li>
                        </ol>
                        <p className="paragon-task5-click-next-text">Click next to continue.</p>
                    </div>
                </div>
            </div>
        </div>
    );

    // Screen 2: Choose Option Selection Screen matching Screenshot 2
    const renderTask5Screen2 = () => {
        const fullPrompt = [
            currentQuestion?.questionTitle,
            currentQuestion?.questionContent
        ].filter(Boolean).join(' ');

        return (
            <div className="paragon-speaking-card">
                {/* Prompt Row */}
                <div className="paragon-speaking-prompt-container">
                    <span className="paragon-info-badge" style={{ marginTop: 2, flexShrink: 0 }}>i</span>
                    <div className="paragon-speaking-prompt-text">
                        <div className="paragon-speaking-prompt-content" style={{ fontSize: 15, lineHeight: 1.6 }}>
                            {fullPrompt || "You find two suitable options. Using the pictures and information below, choose the option that you prefer. In the next section, you will need to persuade your business partner that your choice is the better choice."}
                        </div>
                    </div>
                </div>

                {/* Option Selection Grid (2 Cards Side-by-Side) */}
                <div className="paragon-task5-selection-grid">
                    {/* Option 1 */}
                    <div
                        className={`paragon-task5-select-card ${selectedImage === 'img1' ? 'selected' : ''}`}
                        onClick={() => setSelectedImage('img1')}
                    >
                        <div className="paragon-task5-img-container">
                            <img
                                src={getSpeakingImageUrl(5, currentQuestion?.img)}
                                alt="Option 1"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = `https://engdemyeducation.com/speakingimages/Task5/${currentQuestion?.img}`;
                                }}
                            />
                        </div>
                        {renderFormattedCaption(currentQuestion?.imgCaption)}
                    </div>

                    {/* Option 2 */}
                    <div
                        className={`paragon-task5-select-card ${selectedImage === 'img2' ? 'selected' : ''}`}
                        onClick={() => setSelectedImage('img2')}
                    >
                        <div className="paragon-task5-img-container">
                            <img
                                src={getSpeakingImageUrl(5, currentQuestion?.img2)}
                                alt="Option 2"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = `https://engdemyeducation.com/speakingimages/Task5/${currentQuestion?.img2}`;
                                }}
                            />
                        </div>
                        {renderFormattedCaption(currentQuestion?.img2Caption)}
                    </div>
                </div>
            </div>
        );
    };

    // Screen 3: Comparison & Persuading Screen matching Screenshot 3
    const renderTask5Screen3 = () => {
        const partnerTitle = getPartnerChoiceTitle();
        const isUserOption2 = selectedImage === 'img2';
        const userImg = isUserOption2 ? currentQuestion?.img2 : currentQuestion?.img;
        const userCaption = isUserOption2 ? currentQuestion?.img2Caption : currentQuestion?.imgCaption;

        return (
            <div className="paragon-speaking-card">
                {/* Prompt Row */}
                <div className="paragon-speaking-prompt-container">
                    <span className="paragon-info-badge" style={{ marginTop: 2, flexShrink: 0 }}>i</span>
                    <div className="paragon-speaking-prompt-text">
                        <div className="paragon-speaking-prompt-content" style={{ fontSize: 15, lineHeight: 1.6, fontWeight: 700, color: '#1a4971' }}>
                            {currentQuestion?.questionContentExtra || "Your business partner is suggesting another option. Persuade him that the option you chose is more suitable by comparing the two."}
                        </div>
                    </div>
                </div>

                {/* Side-by-Side Comparison Cards matching Screenshot 3 */}
                <div className="paragon-task5-compare-grid">
                    {/* Partner's Choice Card */}
                    <div className="paragon-task5-compare-card partner-choice">
                        <div className="paragon-task5-compare-header">
                            {partnerTitle}
                        </div>
                        <div className="paragon-task5-img-container">
                            <img
                                src={getSpeakingImageUrl(5, currentQuestion?.img3)}
                                alt="Partner Choice"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = `https://engdemyeducation.com/speakingimages/Task5/${currentQuestion?.img3}`;
                                }}
                            />
                        </div>
                        {renderFormattedCaption(currentQuestion?.img3Caption)}
                    </div>

                    {/* User's Choice Card with green styling */}
                    <div className="paragon-task5-compare-card user-choice">
                        <div className="paragon-task5-compare-header user-header">
                            Your Choice
                        </div>
                        <div className="paragon-task5-img-container">
                            <img
                                src={getSpeakingImageUrl(5, userImg)}
                                alt="Your Choice"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = `https://engdemyeducation.com/speakingimages/Task5/${userImg}`;
                                }}
                            />
                        </div>
                        {renderFormattedCaption(userCaption)}
                    </div>
                </div>

                {/* Timer and Recording Section */}
                <div className="paragon-speaking-timer-wrapper">
                    <div className="paragon-speaking-timer-box">
                        <div className="paragon-speaking-clock-circle">
                            <FaRegClock size={28} color={isRecording ? "#d9534f" : "#6c757d"} />
                        </div>
                        <div className="paragon-speaking-timer-info">
                            <div className="paragon-speaking-timer-label">
                                {prepareTime > 0 ? "Preparation Time" : isRecording ? "Recording Time" : isRecordingDone ? "Recording Complete" : "Preparation Time"}
                            </div>
                            <div
                                className="paragon-speaking-timer-digits"
                                style={{ color: isRecording ? '#d9534f' : '#005a9c' }}
                            >
                                {prepareTime > 0 ? prepareTime : isRecording ? recordingTime : 0}
                            </div>
                        </div>
                    </div>

                    {/* Progress bar during recording */}
                    {isRecording && (
                        <div className="paragon-speaking-progress-container">
                            <progress
                                className="speaking-test-progress-bar"
                                value={(currentQuestion?.recordingTime || 60) - recordingTime}
                                max={currentQuestion?.recordingTime || 60}
                            />
                        </div>
                    )}

                    {/* Note */}
                    <div className="paragon-speaking-note">
                        *NOTE: Recording will be done automatically and score will be added at the end of the test.
                    </div>

                    {/* Sample response button */}
                    <button
                        type="button"
                        className="paragon-speaking-sample-btn"
                        onClick={() => setShowSampleModal(true)}
                    >
                        Click here to view sample responses.
                    </button>

                    {/* Audio Preview & Evaluation status */}
                    {audioUrl && (
                        <div className="audio-preview" style={{ width: '100%', marginTop: 24 }}>
                            <p style={{ fontWeight: 600, color: '#1a4971' }}>Preview your recording:</p>
                            <audio controls src={audioUrl} style={{ width: '100%', maxWidth: 440 }} />
                            {isLoading ? (
                                <div style={{ marginTop: 15 }}>
                                    <LoadingAnimation />
                                    <p style={{ color: '#005a9c', fontWeight: 600 }}>Analyzing response and calculating score...</p>
                                </div>
                            ) : (
                                <div style={{ marginTop: 15 }}>
                                    {transcription && !preCheckFailed && (
                                        <div className="transcription-preview" style={{ textAlign: 'left', maxWidth: 650, margin: '15px auto', padding: '12px 18px', backgroundColor: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                                            <strong style={{ color: '#1a4971' }}>Transcription:</strong>
                                            <p style={{ margin: '6px 0 0', color: '#334155', fontStyle: 'italic' }}>"{transcription}"</p>
                                        </div>
                                    )}
                                    {analysis && preCheckFailed && (
                                        <div className="precheck-encouragement-card" style={{ maxWidth: 650, margin: '15px auto' }}>
                                            <div className="encouragement-icon">
                                                {analysis.startsWith("SILENCE_ISSUE") ? '🎤' : analysis.startsWith("LANGUAGE_ISSUE") ? '🌍' : '📝'}
                                            </div>
                                            <div className="encouragement-body">
                                                {analysis.replace(/^(SILENCE_ISSUE|LANGUAGE_ISSUE|LENGTH_ISSUE):\s*/, '').split('\n').map((line, i) => (
                                                    <p key={i} className={line.startsWith('💡') || line.startsWith('•') ? 'encouragement-tip' : line.startsWith('You') || line.startsWith('Keep') ? 'encouragement-cta' : ''}>
                                                        {line}
                                                    </p>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    

    // Calculate overall average score from all tasks
    const calculateOverallScore = () => {
        const taskNumbers = Object.keys(allTaskResults);
        if (taskNumbers.length === 0) return 0;

        let totalScore = 0;
        let count = 0;

        taskNumbers.forEach(taskNum => {
            const result = allTaskResults[taskNum];
            if (result.scores && result.scores.overallScore) {
                totalScore += result.scores.overallScore;
                count++;
            }
        });

        return count > 0 ? (totalScore / count).toFixed(1) : 0;
    };

    // Render the feedback page after test completion
    const renderFeedbackPage = () => {
        const overallAvg = calculateOverallScore();
        const taskNumbers = Object.keys(allTaskResults).sort((a, b) => parseInt(a) - parseInt(b));

        return (
            <section className="speaking-test-section feedback-section">
                <div className="feedback-container">
                    {/* Celebration Header */}
                    <div className="celebration-header">
                        <div className="confetti-wrapper">
                            <div className="confetti"></div>
                            <div className="confetti"></div>
                            <div className="confetti"></div>
                            <div className="confetti"></div>
                            <div className="confetti"></div>
                        </div>
                        <h1 className="celebration-title">🎉 Congratulations! 🎉</h1>
                        <p className="celebration-subtitle">You've completed all 8 speaking tasks!</p>

                        {/* Overall Score Circle */}
                        <div className="overall-score-circle">
                            <div className="score-ring">
                                <span className="score-value">{overallAvg}</span>
                                <span className="score-label">Average Score</span>
                            </div>
                        </div>
                    </div>

                    {/* Full ScoreGraph for Overall Summary */}
                    {taskNumbers.length > 0 && (
                        <div className="overall-summary-section">
                            <h2 className="summary-title">📈 Overall Performance Summary</h2>
                            <div className="summary-graph-container">
                                <ScoreGraph scores={{
                                    taskFulfillmentScore: taskNumbers.reduce((sum, t) => sum + (allTaskResults[t]?.scores?.taskFulfillmentScore || 0), 0) / taskNumbers.length,
                                    coherenceScore: taskNumbers.reduce((sum, t) => sum + (allTaskResults[t]?.scores?.coherenceScore || 0), 0) / taskNumbers.length,
                                    vocabularyScore: taskNumbers.reduce((sum, t) => sum + (allTaskResults[t]?.scores?.vocabularyScore || 0), 0) / taskNumbers.length,
                                    grammarScore: taskNumbers.reduce((sum, t) => sum + (allTaskResults[t]?.scores?.grammarScore || 0), 0) / taskNumbers.length,
                                    overallScore: parseFloat(overallAvg),
                                    taskFulfillmentJustification: 'Average across all tasks',
                                    coherenceJustification: 'Average across all tasks',
                                    vocabularyJustification: 'Average across all tasks',
                                    grammarJustification: 'Average across all tasks',
                                    overallJustification: 'Combined performance score'
                                }} />
                            </div>
                        </div>
                    )}

                    {/* Task Results Cards */}
                    <div className="task-results-grid">
                        {taskNumbers.map((taskNum, index) => {
                            const result = allTaskResults[taskNum];
                            return (
                                <div
                                    key={taskNum}
                                    className="task-result-card"
                                    style={{ animationDelay: `${index * 0.1}s` }}
                                >
                                    <div className="task-card-header">
                                        <span className="task-number-badge">Task {taskNum}</span>
                                        <span className="task-title">{result.taskTitle}</span>
                                        {result.scores?.overallScore && (
                                            <span className="task-score-badge">
                                                {result.scores.overallScore}/12
                                            </span>
                                        )}
                                    </div>

                                    <div className="task-card-body">
                                        {/* Transcription Section */}
                                        <div className="transcription-section">
                                            <h4>📝 Your Response:</h4>
                                            <p className="transcription-text">
                                                {result.transcription || 'No transcription available'}
                                            </p>
                                        </div>

                                        {/* Score Breakdown */}
                                        {result.scores && Object.keys(result.scores).length > 0 && (
                                            <div className="score-breakdown">
                                                <h4>📊 Score Breakdown:</h4>
                                                <div className="score-bars">
                                                    <div className="score-bar-item">
                                                        <span className="score-bar-label">Task Fulfillment</span>
                                                        <div className="score-bar-track">
                                                            <div
                                                                className="score-bar-fill fulfillment"
                                                                style={{ width: `${(result.scores.taskFulfillmentScore || 0) / 12 * 100}%` }}
                                                            ></div>
                                                        </div>
                                                        <span className="score-bar-value">{result.scores.taskFulfillmentScore || 0}</span>
                                                    </div>
                                                    <div className="score-bar-item">
                                                        <span className="score-bar-label">Coherence</span>
                                                        <div className="score-bar-track">
                                                            <div
                                                                className="score-bar-fill coherence"
                                                                style={{ width: `${(result.scores.coherenceScore || 0) / 12 * 100}%` }}
                                                            ></div>
                                                        </div>
                                                        <span className="score-bar-value">{result.scores.coherenceScore || 0}</span>
                                                    </div>
                                                    <div className="score-bar-item">
                                                        <span className="score-bar-label">Vocabulary</span>
                                                        <div className="score-bar-track">
                                                            <div
                                                                className="score-bar-fill vocabulary"
                                                                style={{ width: `${(result.scores.vocabularyScore || 0) / 12 * 100}%` }}
                                                            ></div>
                                                        </div>
                                                        <span className="score-bar-value">{result.scores.vocabularyScore || 0}</span>
                                                    </div>
                                                    <div className="score-bar-item">
                                                        <span className="score-bar-label">Grammar</span>
                                                        <div className="score-bar-track">
                                                            <div
                                                                className="score-bar-fill grammar"
                                                                style={{ width: `${(result.scores.grammarScore || 0) / 12 * 100}%` }}
                                                            ></div>
                                                        </div>
                                                        <span className="score-bar-value">{result.scores.grammarScore || 0}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Motivational Message */}
                    <div className="motivational-section">
                        <div className="motivational-card">
                            <span className="motivational-icon">🌟</span>
                            <h3>Great Job!</h3>
                            <p>
                                {parseFloat(overallAvg) >= 9
                                    ? "Excellent performance! You're well on your way to achieving your CELPIP goals!"
                                    : parseFloat(overallAvg) >= 7
                                        ? "Good work! With a bit more practice, you'll reach even higher scores!"
                                        : "Keep practicing! Every attempt brings you closer to success!"}
                            </p>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="feedback-actions">
                        <button
                            className="action-btn retry-btn"
                            onClick={() => {
                                setTestCompleted(false);
                                setAllTaskResults({});
                                setCurrentIndex(0);
                            }}
                        >
                            🔄 Retry This Test
                        </button>
                        <button
                            className="action-btn home-btn"
                            onClick={() => navigate('/dashboard?section=speaking')}
                        >
                            🏠 Back to Speaking Tests
                        </button>
                        <button
                            className="action-btn dashboard-btn"
                            onClick={() => navigate('/dashboard')}
                        >
                            📊 Go to Dashboard
                        </button>
                    </div>
                </div>
            </section>
        );
    };

    const getTaskSampleResponse = (taskNum) => {
        const samples = {
            '1': {
                title: "Task 1: Giving Advice",
                sample: "Hi Sarah, welcome to the neighbourhood! I'd be delighted to give you some recommendations on local grocery shopping. In my experience, if you're looking for exceptionally fresh produce and organic goods, Farm Fresh Market on Elm Street is your best bet; their fruits, vegetables, and artisan breads are top-tier, although their prices tend to be slightly on the higher side. On the other hand, for routine everyday pantry essentials and household supplies, Metro Superstore on 4th Avenue is unbeatable in terms of affordability and variety, though it gets quite congested during peak evening hours. I would personally suggest avoiding QuickStop on Main Street, as their stock is limited and noticeably marked up. Overall, doing your weekly staples at Metro and grabbing fresh items at Farm Fresh will give you the ideal balance of quality and savings. Let me know if you’d like me to show you around this weekend!",
                commentary: "Demonstrates native-like fluency, sophisticated lexical variety ('exceptionally fresh', 'artisan breads', 'congested during peak hours', 'weekly staples'), natural discourse markers, and clear logical structure fulfilling all prompt requirements (CLB 10–12)."
            },
            '2': {
                title: "Task 2: Talking about a Personal Experience",
                sample: "I would like to share a memorable experience that truly taught me the value of perseverance. A couple of years ago, I decided to take part in our city’s annual half-marathon, despite having very little endurance running experience. During the first few weeks of training, I constantly struggled with fatigue and self-doubt. However, I stayed disciplined, following a strict morning regimen and gradually building up my stamina. On the race day itself, around the fifteen-kilometer mark, my legs began cramping severely and I contemplated giving up. But hearing the crowd’s encouragement propelled me forward, and crossing that finish line was an indescribable feeling of accomplishment. That experience fundamentally reshaped my mindset, proving that consistent effort and mental resilience can overcome seemingly daunting challenges.",
                commentary: "Exemplary narrative pacing, vivid descriptive adjectives, complex grammatical structures, and strong reflective depth (CLB 10–12)."
            },
            '3': {
                title: "Task 3: Describing a Scene",
                sample: "This image depicts a vibrant and bustling outdoor community park on what appears to be a sunny weekend afternoon. In the foreground on the left, a young family has laid out a checkered picnic blanket; the parents are enjoying sandwiches while their toddler plays with a colourful beach ball. Moving toward the centre, two children are enthusiastically riding their bicycles along a paved pathway lined with mature trees. In the background on the right side, there is an open grassy field where a group of teenagers is engaged in a casual game of frisbee. Nearby, an elderly gentleman sitting on a wooden park bench is peacefully reading a newspaper with a golden retriever resting faithfully by his side. The atmosphere is warm, leisurely, and filled with harmonious community interaction.",
                commentary: "Precise spatial prepositions ('in the foreground on the left', 'moving toward the centre', 'in the background on the right'), extensive descriptive vocabulary, and natural spoken cohesion (CLB 10–12)."
            },
            '4': {
                title: "Task 4: Making Predictions",
                sample: "Based on the ongoing activities in this park scene, several things are likely to unfold in the immediate future. First, the two young children riding their bicycles along the path will probably reach the nearby playground area, where they might dismount and join other kids on the swings and slides. Meanwhile, looking at the family having a picnic, once they finish their lunch, they will almost certainly pack up their basket and perhaps stroll over to the duck pond nearby. Additionally, as the afternoon progresses and temperatures cool down slightly, more local residents will likely arrive to walk their pets or jog along the trails. Finally, the teenagers playing frisbee will wrap up their game and likely gather at the nearby refreshment stand to rehydrate before heading home before dusk.",
                commentary: "Excellent use of speculative modals ('likely to unfold', 'will probably reach', 'almost certainly pack up', 'will wrap up'), varied temporal connectives, and coherent forward projections (CLB 10–12)."
            },
            '5': {
                title: "Task 5: Comparing and Persuading",
                sample: "Hi David, I’ve had a chance to carefully compare both options, and I strongly recommend that we go with Option B. While Option A is certainly more affordable initially, Option B offers far superior long-term value, exceptional durability, and higher safety ratings that cannot be overlooked. Furthermore, Option B includes a comprehensive two-year warranty and round-the-clock customer support, which will save us considerable stress and unexpected repair expenses down the road. Even though the upfront investment is slightly higher, the peace of mind and enhanced efficiency make it overwhelmingly the smarter choice. I’m confident that once we put it to use, you’ll agree it was worth every penny. Let's proceed with Option B today so we don't miss out on the promotional discount.",
                commentary: "Highly persuasive tone, well-balanced concession ('While Option A is certainly...'), strong comparative grammar, and assertive, professional closing (CLB 10–12)."
            },
            '6': {
                title: "Task 6: Dealing with a Difficult Situation",
                sample: "Hello Mr. Henderson, I’m calling to speak with you about an ongoing issue regarding the noise levels from your apartment late in the evenings. Over the past couple of weeks, loud music and vacuuming have been occurring past eleven o’clock at night, which has made it very difficult for my family, especially my young children, to get uninterrupted sleep on weeknights. I completely understand that you have a demanding work schedule and might need to take care of chores whenever you get home; however, city bylaws and our building guidelines specify quiet hours starting at ten PM. I wanted to reach out directly and politely ask if it would be possible to keep the volume down and avoid heavy chores after ten. I truly value having a positive relationship with our neighbours, and I hope we can find a reasonable compromise together. Thank you so much for your understanding.",
                commentary: "Masterful diplomacy, polite assertion, respectful register, empathetic hedging ('I completely understand that... however...'), and clear actionable resolution (CLB 10–12)."
            },
            '7': {
                title: "Task 7: Expressing Opinions",
                sample: "In my view, implementing mandatory physical education in secondary schools every day is a tremendously positive measure. First and foremost, daily physical activity plays a critical role in combating rising rates of adolescent obesity and sedentary lifestyles, directly improving cardiovascular health and muscular strength. Secondly, numerous medical studies have established a profound link between physical exercise and improved cognitive function; students who exercise regularly demonstrate enhanced concentration, lower anxiety levels, and superior academic performance in their other subjects. While some critics may argue that daily gym classes detract from core academic instructional time, the physiological and mental benefits far outweigh any scheduling inconveniences. In summary, prioritizing daily physical fitness cultivates lifelong healthy habits and fosters well-rounded student development.",
                commentary: "Advanced argument structuring ('First and foremost', 'Secondly', 'While some critics may argue...'), high-level academic vocabulary, and persuasive justification (CLB 10–12)."
            },
            '8': {
                title: "Task 8: Describing an Unusual Situation",
                sample: "Hello, I'm calling because I’m looking at an extraordinary piece of art in an antique store that I think would look stunning in our living room, but it's quite peculiar and I need to describe it to you! It's a table lamp, about two feet tall, crafted entirely out of sculpted bronze and blown glass. What makes it unique is that the base is shaped like a whimsical, vintage bicycle with oversized gears. Instead of a standard fabric shade, resting on the handlebars is an illuminated spherical globe that glows with warm amber light and features delicate map etchings of ancient continents. The craftsmanship is remarkably intricate, and it gives off a delightful retro-steampunk aesthetic. It’s definitely a conversation starter! Let me take a quick photo and send it to you, but please let me know what you think!",
                commentary: "Rich sensory details, precise descriptive imagery ('sculpted bronze and blown glass', 'whimsical vintage bicycle', 'spherical globe that glows with warm amber light'), natural conversational cadence (CLB 10–12)."
            }
        };

        const tNum = String(taskNum || '1');
        return samples[tNum] || samples['1'];
    };

    // Render Official Paragon Top Header
    const renderExamHeader = () => (
        <div className="paragon-exam-header">
            <div className="paragon-exam-header-left">
                <img src={EngdemyLogo} alt="CELPIP" className="paragon-exam-logo" />
                <span className="paragon-exam-brand">CELPIP</span>
                <span className="paragon-exam-sep">|</span>
                <span
                    className="paragon-exam-study-link"
                    onClick={() => { stopSpeech(); navigate('/dashboard?section=speaking'); }}
                >
                    Study Materials
                </span>
            </div>
            <div className="paragon-exam-header-center" style={{ fontSize: 13.5, color: '#333333', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: 6 }}>Selected Product:</span>
                <select
                    value={selectedTestNumber || 1}
                    onChange={(e) => {
                        stopSpeech();
                        const newTestNum = Number(e.target.value);
                        setSelectedTestNumber(newTestNum);
                        setShowInstructions(true);
                        setCurrentIndex(null);
                        navigate(`/speaking/${newTestNum}`);
                    }}
                    className="paragon-header-select"
                >
                    {[...Array(30)].map((_, idx) => {
                        const num = idx + 1;
                        return (
                            <option key={num} value={num}>
                                CELPIP-General Practice Tests, Set {num}
                            </option>
                        );
                    })}
                </select>
            </div>
            <div className="paragon-exam-header-right" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: '#333333' }}>
                <span style={{ fontSize: 16, color: '#4a5568' }}>⚙</span>
                <span className="paragon-exam-user-email">
                    {(currentUser?.email || currentUser?.name || "MY ACCOUNT").toUpperCase()} ▾
                </span>
            </div>
        </div>
    );

    // Render Official Paragon Subbar with NEXT and Timer
    const renderExamSubBar = () => {
        const testNum = selectedTestNumber || 1;
        let subbarTitle = `Practice Test ${testNum} - Speaking`;
        if (!showInstructions && currentQuestion) {
            const taskNum = currentQuestion.taskNumber;
            const taskTitle = currentQuestion.taskTitle || (taskNum ? `Task ${taskNum}` : 'Speaking Task');
            subbarTitle = `Practice Test ${testNum} - Speaking Task ${taskNum}: ${taskTitle}`;
        }

        const isTask5 = String(currentQuestion?.taskNumber) === '5';

        return (
            <div className="paragon-exam-subbar">
                <div className="paragon-exam-subbar-title">
                    {subbarTitle}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    {!showInstructions && !testCompleted && (
                        <>
                            {isTask5 ? (
                                <>
                                    {task5Stage === 1 && null}
                                    {task5Stage === 2 && (
                                        <span style={{ fontSize: 13.5, color: '#333333', fontWeight: 500 }}>
                                            Preparation: <strong style={{ color: '#000000', fontWeight: 800 }}>
                                                {imageSelectionTime !== null ? imageSelectionTime : 60} seconds
                                            </strong>
                                        </span>
                                    )}
                                    {task5Stage === 3 && (
                                        <span style={{ fontSize: 13.5, color: '#333333', fontWeight: 500 }}>
                                            Preparation: <strong style={{ color: '#000000', fontWeight: 800 }}>
                                                {prepareTime} seconds
                                            </strong>
                                            {' '}Recording: <strong style={{ color: '#000000', fontWeight: 800 }}>
                                                {recordingTime} seconds
                                            </strong>
                                        </span>
                                    )}
                                </>
                            ) : (
                                <span style={{ fontSize: 13.5, color: '#333333', fontWeight: 500 }}>
                                    Preparation: <strong style={{ color: '#000000', fontWeight: 800 }}>
                                        {currentQuestion?.preparationTime || 30} seconds
                                    </strong>
                                    {' '}Recording: <strong style={{ color: '#000000', fontWeight: 800 }}>
                                        {currentQuestion?.recordingTime || 90} seconds
                                    </strong>
                                </span>
                            )}
                        </>
                    )}
                    <button className="paragon-exam-next-btn" onClick={handleGlobalNext}>
                        NEXT
                    </button>
                </div>
            </div>
        );
    };

    // Render Official Speaking Instructions Screen
    const renderInstructionsPage = () => (
        <div className="paragon-instructions-wrapper" style={{ width: '100%', maxWidth: 980 }}>
            <div className="paragon-instructions-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
                    <div className="paragon-instructions-header" style={{ marginBottom: 0 }}>
                        <span className="paragon-info-badge">i</span>
                        <h2 className="paragon-instructions-title" style={{ fontSize: 21, fontWeight: 800, color: '#1a4971' }}>
                            Speaking Test Instructions
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={toggleSpeech}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            backgroundColor: isSpeaking ? '#005a9c' : '#ffffff',
                            color: isSpeaking ? '#ffffff' : '#000000',
                            border: '1.5px solid #000000',
                            borderRadius: 4,
                            padding: '6px 14px',
                            fontSize: 13,
                            fontWeight: 700,
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
                        }}
                        title="Read instructions aloud"
                    >
                        <span>{isSpeaking ? '🔊 Reading Aloud...' : '▶ Listen Instructions'}</span>
                    </button>
                </div>

                <ul className="paragon-instructions-list">
                    <li className="paragon-instruction-item" style={{ borderBottom: 'none', padding: '10px 0 16px' }}>
                        <span
                            className="paragon-instruction-bullet"
                            style={{ backgroundColor: '#718096', width: 8, height: 8, marginTop: 9 }}
                        ></span>
                        <span
                            className="paragon-instruction-text"
                            style={{ fontSize: 16.5, color: '#1a4971', lineHeight: 1.65, fontWeight: 700 }}
                        >
                            In this practice test, recording will be done for each of your speaking tasks, and a score will be added upon completion. You will also receive detailed AI evaluation and performance analysis.
                        </span>
                    </li>

                    <li className="paragon-instruction-item" style={{ borderBottom: 'none', padding: '10px 0 16px' }}>
                        <span
                            className="paragon-instruction-bullet"
                            style={{ backgroundColor: '#718096', width: 8, height: 8, marginTop: 9 }}
                        ></span>
                        <span
                            className="paragon-instruction-text"
                            style={{ fontSize: 16.5, color: '#1a4971', lineHeight: 1.65, fontWeight: 700 }}
                        >
                            For this practice test, an automated preparation timer and recording timer will guide you to ensure you complete each task within the given time.
                        </span>
                    </li>

                    <li className="paragon-instruction-item" style={{ borderBottom: 'none', padding: '10px 0 16px' }}>
                        <span
                            className="paragon-instruction-bullet"
                            style={{ backgroundColor: '#718096', width: 8, height: 8, marginTop: 9 }}
                        ></span>
                        <span
                            className="paragon-instruction-text"
                            style={{ fontSize: 16.5, color: '#1a4971', lineHeight: 1.65, fontWeight: 700 }}
                        >
                            Audio recording will be done automatically using your computer microphone. Please speak clearly into your microphone when the recording timer begins.
                        </span>
                    </li>

                    <li className="paragon-instruction-item" style={{ borderBottom: 'none', padding: '10px 0 16px' }}>
                        <span
                            className="paragon-instruction-bullet"
                            style={{ backgroundColor: '#718096', width: 8, height: 8, marginTop: 9 }}
                        ></span>
                        <span
                            className="paragon-instruction-text"
                            style={{ fontSize: 16.5, color: '#1a4971', lineHeight: 1.65, fontWeight: 700 }}
                        >
                            On the official test, if you do not finish a task in the time provided, the screen will move to the next task. You cannot go back to the previous task. However, in this practice test, in order to move forward in the test you must click on "NEXT."
                        </span>
                    </li>
                </ul>
            </div>
        </div>
    );

    // Helper to parse Task 6 into scenario and EITHER / OR choices matching Screenshot 6
    const parseTask6 = (content, title) => {
        if (!content) return { intro: title || '', opt1: '', opt2: '' };

        // Case 1: Explicit EITHER and OR in content
        if (/\bEITHER\b/i.test(content) && /\bOR\b/i.test(content) && (content.includes('\n') || content.includes('Choose ONE'))) {
            const parts = content.split(/\bEITHER\b[:\s]*/i);
            const intro = parts[0].replace(/Choose ONE:?/i, '').trim();
            const sub = (parts[1] || '').split(/\bOR\b[:\s]*/i);
            return {
                intro: intro || title || '',
                opt1: (sub[0] || '').trim(),
                opt2: (sub[1] || '').trim()
            };
        }

        // Case 2: Standard Celpip Task 6: "Choose one person, either [P1] or [P2], to explain... why you can't attend [E1] or [E2]."
        const eitherMatch = content.match(/either\s+(.*?)\s+or\s+(.*?),?\s+to\s+explain\s+(?:to\s+(?:him\/her|them)\s+)?why\s+(.*)/i);
        if (eitherMatch) {
            let p1 = eitherMatch[1].trim();
            let p2 = eitherMatch[2].trim();
            if (!p1.toLowerCase().startsWith('your ') && !p1.toLowerCase().startsWith('the ')) p1 = 'your ' + p1;
            if (!p2.toLowerCase().startsWith('your ') && !p2.toLowerCase().startsWith('the ')) p2 = 'your ' + p2;

            const rest = eitherMatch[3].trim();
            const attendMatch = rest.match(/(?:you\s+(?:can\'t|cannot)\s+attend\s+)?(.*?)\s+or\s+(.*)/i);
            if (attendMatch) {
                let e1 = attendMatch[1].trim();
                let e2 = attendMatch[2].replace(/\.$/, '').trim();
                if (!e1.toLowerCase().startsWith('the ') && !e1.toLowerCase().startsWith('a ')) e1 = 'the ' + e1;
                if (!e2.toLowerCase().startsWith('the ') && !e2.toLowerCase().startsWith('a ')) e2 = 'the ' + e2;
                return {
                    intro: title || "You must deal with a difficult situation.",
                    opt1: `Explain to ${p1} why you cannot attend ${e1}.`,
                    opt2: `Explain to ${p2} why you cannot attend ${e2}.`
                };
            }
        }

        return {
            intro: title || "You must deal with a difficult situation.",
            opt1: 'Explain why you choose the first option.',
            opt2: 'Explain why you choose the second option.'
        };
    };

    // Render Active Speaking Test Card matching official Paragon CELPIP UI screenshots
    const renderActiveSpeakingTest = () => {
        if (!currentQuestion) {
            return (
                <div style={{ padding: 60, textAlign: 'center', color: '#666', width: '100%' }}>
                    <p style={{ fontSize: 18, fontWeight: 600 }}>Loading Speaking Tasks...</p>
                </div>
            );
        }

        const taskNum = String(currentQuestion.taskNumber);

        // Special 3-Screen flow for Task 5
        if (taskNum === '5') {
            if (task5Stage === 1) return renderTask5Screen1();
            if (task5Stage === 2) return renderTask5Screen2();
            if (task5Stage === 3) return renderTask5Screen3();
        }

        const isSelectionPhase = imageSelectionTime !== null && imageSelectionTime > 0 && !showThirdImage;
        const currentTimerValue = isSelectionPhase
            ? imageSelectionTime
            : prepareTime > 0
            ? prepareTime
            : isRecording
            ? recordingTime
            : 0;

        const currentTimerLabel = isSelectionPhase
            ? "Selection Time"
            : prepareTime > 0
            ? "Preparation Time"
            : isRecording
            ? "Recording Time"
            : isRecordingDone
            ? "Recording Complete"
            : "Preparation Time";

        // Official Paragon Rounded Grey Timer Box
        const renderSpeakingTimerBox = () => (
            <div className="paragon-speaking-timer-box">
                <div className="paragon-speaking-clock-circle">
                    <FaRegClock size={28} color={isRecording ? "#d9534f" : "#6c757d"} />
                </div>
                <div className="paragon-speaking-timer-info">
                    <div className="paragon-speaking-timer-label">
                        {currentTimerLabel}
                    </div>
                    <div
                        className="paragon-speaking-timer-digits"
                        style={{ color: isRecording ? '#d9534f' : '#005a9c' }}
                    >
                        {currentTimerValue}
                    </div>
                </div>
            </div>
        );

        // Controls below prompt / image (progress bar, note, sample responses button, audio preview)
        const renderSpeakingControlsAndPreview = () => (
            <>
                {/* Progress bar during recording */}
                {isRecording && (
                    <div className="paragon-speaking-progress-container" style={{ margin: '14px auto 0' }}>
                        <progress
                            className="speaking-test-progress-bar"
                            value={(currentQuestion?.recordingTime || 90) - recordingTime}
                            max={currentQuestion?.recordingTime || 90}
                        />
                    </div>
                )}

                {/* Note matching official CELPIP screenshot */}
                <div className="paragon-speaking-note">
                    *NOTE: Recording will be done automatically and score will be added at the end of the test.
                </div>

                {/* Wide button matching screenshot */}
                <button
                    type="button"
                    className="paragon-speaking-sample-btn"
                    onClick={() => setShowSampleModal(true)}
                >
                    Click here to view sample responses.
                </button>

                {/* Audio Preview & Evaluation status when recording completes */}
                {audioUrl && (
                    <div className="audio-preview" style={{ width: '100%', marginTop: 24 }}>
                        <p style={{ fontWeight: 600, color: '#1a4971' }}>Preview your recording:</p>
                        <audio controls src={audioUrl} style={{ width: '100%', maxWidth: 440 }} />
                        {isLoading ? (
                            <div style={{ marginTop: 15 }}>
                                <LoadingAnimation />
                                <p style={{ color: '#005a9c', fontWeight: 600 }}>Analyzing response and calculating score...</p>
                            </div>
                        ) : (
                            <div style={{ marginTop: 15 }}>
                                {transcription && !preCheckFailed && (
                                    <div className="transcription-preview" style={{ textAlign: 'left', maxWidth: 650, margin: '15px auto', padding: '12px 18px', backgroundColor: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                                        <strong style={{ color: '#1a4971' }}>Transcription:</strong>
                                        <p style={{ margin: '6px 0 0', color: '#334155', fontStyle: 'italic' }}>"{transcription}"</p>
                                    </div>
                                )}
                                {analysis && preCheckFailed ? (
                                    <div className="precheck-encouragement-card" style={{ maxWidth: 650, margin: '15px auto' }}>
                                        <div className="encouragement-icon">
                                            {analysis.startsWith("SILENCE_ISSUE") ? '🎤' : analysis.startsWith("LANGUAGE_ISSUE") ? '🌍' : '📝'}
                                        </div>
                                        <div className="encouragement-body">
                                            {analysis.replace(/^(SILENCE_ISSUE|LANGUAGE_ISSUE|LENGTH_ISSUE):\s*/, '').split('\n').map((line, i) => (
                                                <p key={i} className={line.startsWith('💡') || line.startsWith('•') ? 'encouragement-tip' : line.startsWith('You') || line.startsWith('Keep') ? 'encouragement-cta' : ''}>
                                                    {line}
                                                </p>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    analysis && scores && Object.keys(scores).length > 0 && (
                                        <button
                                            type="button"
                                            className="paragon-exam-next-btn"
                                            style={{ marginTop: 10 }}
                                            onClick={() => setShowGraph(true)}
                                        >
                                            View Task {currentQuestion?.taskNumber} Score & Analysis
                                        </button>
                                    )
                                )}
                                {showGraph && (
                                    <div className="popup-overlay">
                                        <div className="popup-content">
                                            <button className="close-button" onClick={() => setShowGraph(false)}>
                                                &times;
                                            </button>
                                            <ScoreGraph scores={scores} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </>
        );

        // Tasks 3, 4, 8: Image-based tasks with Side-by-Side Split Layout matching Screenshot 8
        if (['3', '4', '8'].includes(taskNum) && (currentQuestion?.img || currentQuestion?.img2)) {
            const fullPrompt = [currentQuestion?.questionTitle, currentQuestion?.questionContent]
                .filter(Boolean)
                .join(' ');

            return (
                <div className="paragon-speaking-card">
                    {/* Prompt Row */}
                    <div className="paragon-speaking-prompt-container">
                        <span className="paragon-info-badge" style={{ marginTop: 2, flexShrink: 0 }}>i</span>
                        <div className="paragon-speaking-prompt-text">
                            <div className="paragon-speaking-prompt-content" style={{ fontSize: 16, fontWeight: 700, color: '#1a4971', lineHeight: 1.55 }}>
                                {fullPrompt || "Answer the speaking prompt."}
                            </div>
                        </div>
                    </div>

                    {/* Split Layout: Image on Left, Centered Timer on Right */}
                    <div className="paragon-speaking-split-layout">
                        <div className="paragon-speaking-split-left">
                            <img
                                src={getSpeakingImageUrl(currentQuestion.taskNumber, currentQuestion.img || currentQuestion.img2)}
                                alt={`Task ${currentQuestion.taskNumber}`}
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = `https://engdemyeducation.com/speakingimages/Task${currentQuestion.taskNumber}/${currentQuestion.img || currentQuestion.img2}`;
                                }}
                            />
                        </div>
                        <div className="paragon-speaking-split-right">
                            {renderSpeakingTimerBox()}
                        </div>
                    </div>

                    {/* Note, Sample responses button, Audio preview */}
                    {renderSpeakingControlsAndPreview()}
                </div>
            );
        }

        // Task 6: Dealing with a Difficult Situation matching Screenshot 6
        if (taskNum === '6') {
            const t6 = parseTask6(currentQuestion?.questionContent, currentQuestion?.questionTitle);

            return (
                <div className="paragon-speaking-card">
                    {/* Scenario Prompt */}
                    <div className="paragon-speaking-prompt-container">
                        <span className="paragon-info-badge" style={{ marginTop: 2, flexShrink: 0 }}>i</span>
                        <div className="paragon-speaking-prompt-text">
                            <div className="paragon-speaking-prompt-content" style={{ fontSize: 15.5, fontWeight: 700, color: '#1a4971', lineHeight: 1.55 }}>
                                {t6.intro}
                            </div>
                        </div>
                    </div>

                    {/* Choose ONE / EITHER / OR choices block */}
                    <div className="paragon-task6-choices-block" style={{ paddingLeft: 18, marginTop: 16 }}>
                        <div className="paragon-task6-choose-one" style={{ fontWeight: 700, color: '#2d3748', marginBottom: 8, fontSize: 15 }}>
                            Choose ONE:
                        </div>
                        <div className="paragon-task6-label" style={{ fontWeight: 700, color: '#1a202c', marginTop: 10, fontSize: 14.5 }}>
                            EITHER
                        </div>
                        <div className="paragon-task6-option" style={{ color: '#2d3748', margin: '4px 0 10px 0', fontSize: 14.5, lineHeight: 1.5 }}>
                            {t6.opt1}
                        </div>
                        <div className="paragon-task6-label" style={{ fontWeight: 700, color: '#1a202c', marginTop: 10, fontSize: 14.5 }}>
                            OR
                        </div>
                        <div className="paragon-task6-option" style={{ color: '#2d3748', margin: '4px 0 10px 0', fontSize: 14.5, lineHeight: 1.5 }}>
                            {t6.opt2}
                        </div>
                    </div>

                    {/* Centered Timer Box */}
                    <div className="paragon-speaking-timer-wrapper">
                        {renderSpeakingTimerBox()}
                    </div>

                    {/* Note, Sample responses button, Audio preview */}
                    {renderSpeakingControlsAndPreview()}
                </div>
            );
        }

        // Task 7: Expressing Opinions matching Screenshot 7
        if (taskNum === '7') {
            const qBody = [currentQuestion?.questionTitle, currentQuestion?.questionContent]
                .filter(Boolean)
                .join(' ')
                .replace(/^Question:\s*/i, '');

            return (
                <div className="paragon-speaking-card">
                    {/* Header: Answer the following question */}
                    <div className="paragon-speaking-prompt-container">
                        <span className="paragon-info-badge" style={{ marginTop: 2, flexShrink: 0 }}>i</span>
                        <div className="paragon-speaking-prompt-text">
                            <div style={{ fontSize: 16.5, fontWeight: 700, color: '#1a4971', marginBottom: 14 }}>
                                Answer the following question.
                            </div>
                            <div style={{ fontSize: 15, color: '#2d3748', lineHeight: 1.6, paddingLeft: 18 }}>
                                <strong style={{ color: '#1a202c' }}>Question:</strong> {qBody}
                            </div>
                        </div>
                    </div>

                    {/* Centered Timer Box */}
                    <div className="paragon-speaking-timer-wrapper">
                        {renderSpeakingTimerBox()}
                    </div>

                    {/* Note, Sample responses button, Audio preview */}
                    {renderSpeakingControlsAndPreview()}
                </div>
            );
        }

        // Default: Tasks 1, 2 (Text-based prompts)
        const fullPrompt = [currentQuestion?.questionTitle, currentQuestion?.questionContent]
            .filter(Boolean)
            .join(' ');

        return (
            <div className="paragon-speaking-card">
                {/* Prompt Row */}
                <div className="paragon-speaking-prompt-container">
                    <span className="paragon-info-badge" style={{ marginTop: 2, flexShrink: 0 }}>i</span>
                    <div className="paragon-speaking-prompt-text">
                        <div className="paragon-speaking-prompt-content" style={{ fontSize: 16, fontWeight: 700, color: '#1a4971', lineHeight: 1.55 }}>
                            {fullPrompt || 'Answer the speaking prompt.'}
                        </div>
                    </div>
                </div>

                {/* Centered Timer Box */}
                <div className="paragon-speaking-timer-wrapper">
                    {renderSpeakingTimerBox()}
                </div>

                {/* Note, Sample responses button, Audio preview */}
                {renderSpeakingControlsAndPreview()}
            </div>
        );
    };

    // Render Official Paragon Bottom Bar with Task Tabs and BACK
    const renderExamBottomBar = () => (
        <div className="paragon-exam-bottombar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Task tabs 1 to 8 in the bottom */}
            <div className="paragon-bottom-task-tabs">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((t) => {
                    const isActive = !showInstructions && currentQuestion && String(currentQuestion.taskNumber) === String(t);
                    return (
                        <button
                            key={t}
                            type="button"
                            className={`paragon-bottom-task-btn ${isActive ? 'active' : ''}`}
                            onClick={() => {
                                stopSpeech();
                                if (showInstructions) {
                                    setShowInstructions(false);
                                }
                                if (isRecording) {
                                    debouncedStopActualRecording();
                                }
                                const index = questions.findIndex(q => String(q.taskNumber) === String(t));
                                if (index !== -1) {
                                    if (String(t) === '5') {
                                        setTask5Stage(1);
                                        setSelectedImage(null);
                                        setShowThirdImage(false);
                                        setImageSelectionTime(null);
                                        setPrepareTime(0);
                                    }
                                    setCurrentIndex(index);
                                }
                            }}
                        >
                            Task {t}
                        </button>
                    );
                })}
            </div>

            {/* BACK button on right */}
            <button className="paragon-exam-back-btn" onClick={handleGlobalBack}>
                BACK
            </button>
        </div>
    );

    // Render Official Footer
    const renderExamFooter = () => (
        <div className="paragon-exam-footer" style={{
            textAlign: 'center',
            padding: '16px 20px 24px',
            fontSize: 12.5,
            color: '#6c757d',
            backgroundColor: '#eaedf1'
        }}>
            Copyright 2025 - Engdemy | www.celpip.ca | Contact Us
        </div>
    );

    // Render Sample Responses Modal
    const renderSampleModal = () => {
        if (!showSampleModal) return null;
        const sampleObj = getTaskSampleResponse(currentQuestion?.taskNumber);

        return (
            <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 1000, padding: 20
            }}>
                <div style={{
                    backgroundColor: '#ffffff', borderRadius: 8,
                    padding: '28px 32px', maxWidth: 720, width: '100%',
                    maxHeight: '88vh', overflowY: 'auto',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
                }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <div>
                            <h3 style={{ margin: 0, color: '#1a4971', fontSize: 19, fontWeight: 800 }}>
                                {sampleObj.title} — CLB Level 10–12
                            </h3>
                            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
                                High-scoring native spoken demonstration
                            </p>
                        </div>
                        <button
                            onClick={() => { stopSpeech(); setShowSampleModal(false); }}
                            style={{ background: 'none', border: 'none', fontSize: 26, cursor: 'pointer', color: '#666', lineHeight: 1 }}
                        >
                            &times;
                        </button>
                    </div>

                    {/* Listen Button */}
                    <div style={{ marginBottom: 18 }}>
                        <button
                            type="button"
                            onClick={() => toggleSampleAudio(sampleObj.sample)}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                backgroundColor: sampleAudioPlaying ? '#d9534f' : '#005a9c',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: 4,
                                padding: '8px 18px',
                                fontSize: 13.5,
                                fontWeight: 700,
                                cursor: 'pointer'
                            }}
                        >
                            <span>{sampleAudioPlaying ? '⏹ Stop Audio' : '▶ Listen Sample Response'}</span>
                        </button>
                    </div>

                    {/* Sample Spoken Script */}
                    <div style={{
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: 6,
                        padding: '18px 22px',
                        marginBottom: 18,
                        fontSize: 15,
                        lineHeight: 1.7,
                        color: '#1e293b'
                    }}>
                        <p style={{ margin: 0 }}>
                            "{sampleObj.sample}"
                        </p>
                    </div>

                    {/* Examiner Commentary */}
                    <div style={{
                        backgroundColor: '#f0fdf4',
                        border: '1px solid #86efac',
                        borderRadius: 6,
                        padding: '14px 18px',
                        marginBottom: 20
                    }}>
                        <strong style={{ color: '#15803d', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                            <span>💬</span> Examiner Assessment & Strengths:
                        </strong>
                        <p style={{ margin: 0, fontSize: 13.5, color: '#374151', lineHeight: 1.6 }}>
                            {sampleObj.commentary}
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => { stopSpeech(); setShowSampleModal(false); }}
                        style={{
                            width: '100%',
                            padding: '11px',
                            backgroundColor: '#005a9c',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: 4,
                            fontWeight: 700,
                            cursor: 'pointer',
                            fontSize: 14.5
                        }}
                    >
                        Close
                    </button>
                </div>
            </div>
        );
    };

    if (testCompleted) {
        return (
            <div className="paragon-exam-page-wrapper">
                {renderExamHeader()}
                {renderFeedbackPage()}
                {renderExamFooter()}
            </div>
        );
    }

    return (
        <div className="paragon-exam-page-wrapper">
            {renderExamHeader()}
            {renderExamSubBar()}

            <div className="paragon-exam-body-content">
                {showInstructions ? (
                    renderInstructionsPage()
                ) : (
                    renderActiveSpeakingTest()
                )}
            </div>

            {renderExamBottomBar()}
            {renderExamFooter()}
            {renderSampleModal()}
        </div>
    );
};

const mapStateToProps = (state) => ({ auth: state.auth });

export default connect(mapStateToProps)(SpeakingTest);